import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { Filter, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 48;

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);

  const kategori = params.get("kategori") || "";
  const q = params.get("q") || "";
  const page = parseInt(params.get("sayfa") || "1", 10);

  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const opts = { page, page_size: PAGE_SIZE };
    if (kategori) opts.category = kategori;
    if (q) opts.q = q;
    api.get("/products", { params: opts }).then(r => setData(r.data)).finally(() => setLoading(false));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [kategori, q, page]);

  const activeCat = cats.find(c => c.slug === kategori);
  const setPage = (n) => {
    const nx = { ...Object.fromEntries(params), sayfa: String(n) };
    setParams(nx);
  };
  const changeCat = (slug) => {
    const nx = {};
    if (slug) nx.kategori = slug;
    if (q) nx.q = q;
    setParams(nx);
  };

  const seoTitle = activeCat ? activeCat.name : q ? `"${q}" arama sonuçları` : "Tüm Ürünler — Katalog";
  const seoDesc = activeCat
    ? `${activeCat.name} kategorisinde ${data.total} ürün. ${activeCat.description || ""}`
    : `${data.total} adet orijinal hırdavat ürünü. Kuzey Yapı katalog.`;

  return (
    <div className="kz-container py-10">
      <Seo title={seoTitle} description={seoDesc} path="/katalog"/>
      <nav className="text-xs text-ink-500 mb-4 flex items-center gap-1.5">
        <Link to="/" className="hover:text-ink-900">Ana Sayfa</Link>
        <span>/</span>
        <Link to="/katalog" className="hover:text-ink-900">Katalog</Link>
        {activeCat && <><span>/</span><span className="text-ink-900">{activeCat.name}</span></>}
      </nav>

      <h1 className="font-display text-3xl md:text-4xl font-black mb-2">
        {activeCat ? activeCat.name : q ? `"${q}" için sonuçlar` : "Tüm Ürünler"}
      </h1>
      <p className="text-ink-500 mb-8" data-testid="catalog-total">
        <strong>{data.total}</strong> ürün — Sayfa {data.page} / {data.pages}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="kz-card p-4">
            <div className="flex items-center gap-2 mb-4 text-sm font-bold uppercase tracking-wider">
              <Filter size={14}/> Kategoriler
            </div>
            <ul className="space-y-1 max-h-[60vh] overflow-y-auto">
              <li>
                <button data-testid="filter-all" onClick={() => changeCat("")}
                  className={`w-full text-left py-2 px-2 text-sm hover:bg-ink-100 ${!kategori ? "bg-ink-900 text-white hover:bg-ink-900" : ""}`}>
                  Tümü
                </button>
              </li>
              {cats.map(c => (
                <li key={c.id}>
                  <button data-testid={`filter-${c.slug}`} onClick={() => changeCat(c.slug)}
                    className={`w-full text-left py-2 px-2 text-sm hover:bg-ink-100 ${kategori === c.slug ? "bg-ink-900 text-white hover:bg-ink-900" : ""}`}>
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
              {Array.from({length: 12}).map((_, i) => <div key={`sk-${i}`} className="aspect-square bg-ink-100 animate-pulse"/>)}
            </div>
          ) : data.items.length === 0 ? (
            <div className="kz-card p-12 text-center">
              <p className="text-ink-500">Ürün bulunamadı.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
                {data.items.map(p => <ProductCard key={p.id} product={p}/>)}
              </div>
              {data.pages > 1 && <Pagination page={data.page} pages={data.pages} setPage={setPage}/>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Pagination({ page, pages, setPage }) {
  const range = pageRange(page, pages);
  return (
    <div className="flex items-center justify-center gap-1 mt-10 flex-wrap" data-testid="pagination">
      <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="px-3 py-2 border border-ink-200 hover:border-ink-900 disabled:opacity-40 disabled:cursor-not-allowed">
        <ChevronLeft size={16}/>
      </button>
      {range.map((n, idx) =>
        n === "..." ? (
          <span key={`dots-${idx}`} className="px-3 py-2 text-ink-500">…</span>
        ) : (
          <button key={n} onClick={() => setPage(n)} data-testid={`page-${n}`}
                  className={`min-w-[40px] px-3 py-2 border text-sm font-semibold ${
                    n === page ? "bg-ink-900 text-white border-ink-900" : "border-ink-200 hover:border-ink-900"
                  }`}>
            {n}
          </button>
        )
      )}
      <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages}
              className="px-3 py-2 border border-ink-200 hover:border-ink-900 disabled:opacity-40 disabled:cursor-not-allowed">
        <ChevronRight size={16}/>
      </button>
    </div>
  );
}

function pageRange(current, total) {
  // smart pagination: show 1, ..., current-1, current, current+1, ..., total
  const set = new Set([1, total, current - 1, current, current + 1]);
  const nums = [...set].filter(n => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const n of nums) {
    if (prev && n - prev > 1) out.push("...");
    out.push(n);
    prev = n;
  }
  return out;
}
